<script>
import Layout from "../../layouts/main";
import PageHeader from "../../components/page-header";

export default {
    components: { Layout, PageHeader },
    data() {
        const anioActual = new Date().getFullYear();
        return {
            title: "Uso digitalizadores",
            items: [
                {
                    text: "Digitalizador",
                    href: "/web/digitalizar/asociar"
                },
                {
                    text: "Uso digitalizadores",
                    active: true
                }
            ],
            loading: false,
            error: "",
            anio: anioActual,
            aniosDisponibles: [2026, anioActual].filter((value, index, self) => self.indexOf(value) === index),
            meses: [],
            resumenAnual: {
                total: 0,
                descartadas: 0,
                versiones: [],
                informativos: {
                    plantilla_asistente: 0,
                    asistentes_qr: 0,
                    asistentes_qr_descartados: 0
                }
            },
            mesActual: null,
            proyeccion: {
                fin_anio: 0,
                meses_base: 0
            },
            rangoDisponible: {
                desde: null,
                hasta: null
            },
            costosV3: {
                baseline_paginas: 2493,
                baseline_fecha_utc: null,
                total_paginas: 2493,
                paginas_nuevas: 0,
                estimado_clp_por_pagina: 3,
                estimado_total_clp: 0,
                formula_costo_real: ''
            },
            actualizadoEn: null
        };
    },
    computed: {
        esUsuarioAutorizado() {
            return Number(this.user && this.user.id) === 1;
        },
        totalV2() {
            return this.totalVersion('v2');
        },
        totalV3() {
            return this.totalVersion('v3');
        },
        totalPlantillaAsistente() {
            return Number(this.resumenAnual?.informativos?.plantilla_asistente || 0);
        },
        totalAsistentesQr() {
            return Number(this.resumenAnual?.informativos?.asistentes_qr || 0);
        },
        totalMesActual() {
            return this.mesActual ? Number(this.mesActual.total || 0) : 0;
        }
    },
    methods: {
        totalVersion(version) {
            const item = (this.resumenAnual.versiones || []).find(row => row.key === version);
            return item ? Number(item.total || 0) : 0;
        },
        formatNumber(value) {
            return Number(value || 0).toLocaleString('es-CL');
        },
        formatClp(value) {
            return Number(value || 0).toLocaleString('es-CL', {
                style: 'currency',
                currency: 'CLP',
                maximumFractionDigits: 0
            });
        },
        formatDateTime(value) {
            if (!value) {
                return '-';
            }

            return new Date(value).toLocaleString('es-CL', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        async cargarUso() {
            if (!this.esUsuarioAutorizado) {
                return;
            }

            this.loading = true;
            this.error = "";

            try {
                const response = await axios.get('/api/digitalizar/uso', {
                    params: {
                        anio: this.anio
                    }
                });
                const data = response.data || {};

                if (!data.estado) {
                    this.error = data.mensaje || 'No se pudo cargar el uso de digitalizadores.';
                    return;
                }

                this.anio = data.anio || this.anio;
                this.meses = Array.isArray(data.meses) ? data.meses : [];
                this.resumenAnual = data.resumen_anual || this.resumenAnual;
                this.mesActual = data.mes_actual || null;
                this.proyeccion = data.proyeccion || this.proyeccion;
                this.rangoDisponible = data.rango_disponible || this.rangoDisponible;
                this.costosV3 = data.costos_v3 || this.costosV3;
                this.actualizadoEn = data.actualizado_en || null;
            } catch (error) {
                const status = error && error.response ? error.response.status : null;
                if (status === 403) {
                    window.location.href = '/';
                    return;
                }

                this.error = 'Error al cargar el uso de digitalizadores.';
            } finally {
                this.loading = false;
            }
        }
    },
    created() {
        if (!this.esUsuarioAutorizado) {
            window.location.href = '/';
            return;
        }

        this.cargarUso();
    }
};
</script>

<template>
    <Layout>
        <div v-if="!esUsuarioAutorizado"></div>
        <template v-else>
            <PageHeader :title="title" :items="items" />

            <div class="row mb-3">
                <div class="col-12 d-flex flex-wrap justify-content-between align-items-center">
                    <small class="text-muted mb-2 mb-md-0">
                        Ultima actualizacion: {{ formatDateTime(actualizadoEn) }}
                        <span v-if="rangoDisponible.desde || rangoDisponible.hasta" class="ml-2">
                            | Rango datos: {{ formatDateTime(rangoDisponible.desde) }} - {{ formatDateTime(rangoDisponible.hasta) }}
                        </span>
                    </small>
                    <div class="d-flex align-items-center">
                        <select v-model="anio" class="form-control form-control-sm mr-2" @change="cargarUso" :disabled="loading">
                            <option v-for="year in aniosDisponibles" :key="year" :value="year">{{ year }}</option>
                        </select>
                        <button class="btn btn-sm btn-primary" @click="cargarUso" :disabled="loading">Actualizar</button>
                    </div>
                </div>
            </div>

            <div v-if="error" class="alert alert-danger">{{ error }}</div>

            <div class="row">
                <div class="col-md-2">
                    <div class="card mini-stats-wid">
                        <div class="card-body">
                            <p class="text-muted font-weight-medium mb-2">Total anual</p>
                            <h4 class="mb-0">{{ formatNumber(resumenAnual.total) }}</h4>
                        </div>
                    </div>
                </div>
                <div class="col-md-2">
                    <div class="card mini-stats-wid">
                        <div class="card-body">
                            <p class="text-muted font-weight-medium mb-2">Version 2</p>
                            <h4 class="mb-0">{{ formatNumber(totalV2) }}</h4>
                        </div>
                    </div>
                </div>
                <div class="col-md-2">
                    <div class="card mini-stats-wid">
                        <div class="card-body">
                            <p class="text-muted font-weight-medium mb-2">Version 3</p>
                            <h4 class="mb-0">{{ formatNumber(totalV3) }}</h4>
                        </div>
                    </div>
                </div>
                <div class="col-md-2">
                    <div class="card mini-stats-wid">
                        <div class="card-body">
                            <p class="text-muted font-weight-medium mb-2">Plantilla asistente</p>
                            <h4 class="mb-0">{{ formatNumber(totalPlantillaAsistente) }}</h4>
                        </div>
                    </div>
                </div>
                <div class="col-md-2">
                    <div class="card mini-stats-wid">
                        <div class="card-body">
                            <p class="text-muted font-weight-medium mb-2">Asistentes QR</p>
                            <h4 class="mb-0">{{ formatNumber(totalAsistentesQr) }}</h4>
                        </div>
                    </div>
                </div>
                <div class="col-md-2">
                    <div class="card mini-stats-wid">
                        <div class="card-body">
                            <p class="text-muted font-weight-medium mb-2">Proyeccion fin de ano</p>
                            <h4 class="mb-0">{{ formatNumber(proyeccion.fin_anio) }}</h4>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card border-left border-success">
                <div class="card-body">
                    <div class="d-flex flex-wrap justify-content-between align-items-start mb-3">
                        <div>
                            <h4 class="card-title mb-1">Costo V3 desde optimizacion</h4>
                            <small class="text-muted">
                                Base {{ formatNumber(costosV3.baseline_paginas) }} paginas al
                                {{ formatDateTime(costosV3.baseline_fecha_utc) }}
                            </small>
                        </div>
                        <span class="badge badge-success">Estimacion conservadora</span>
                    </div>
                    <div class="row">
                        <div class="col-md-3 mb-3 mb-md-0">
                            <small class="text-muted d-block">Contador V3 acumulado</small>
                            <strong class="h5">{{ formatNumber(costosV3.total_paginas) }}</strong>
                        </div>
                        <div class="col-md-3 mb-3 mb-md-0">
                            <small class="text-muted d-block">Paginas nuevas</small>
                            <strong class="h5">{{ formatNumber(costosV3.paginas_nuevas) }}</strong>
                        </div>
                        <div class="col-md-3 mb-3 mb-md-0">
                            <small class="text-muted d-block">Estimado por pagina</small>
                            <strong class="h5">{{ formatClp(costosV3.estimado_clp_por_pagina) }}</strong>
                        </div>
                        <div class="col-md-3">
                            <small class="text-muted d-block">Costo estimado acumulado</small>
                            <strong class="h5">{{ formatClp(costosV3.estimado_total_clp) }}</strong>
                        </div>
                    </div>
                    <small class="text-muted d-block mt-3">
                        El costo real se obtiene con: {{ costosV3.formula_costo_real }}. AWS puede publicar cargos con retraso.
                    </small>
                </div>
            </div>

            <div class="row">
                <div class="col-lg-8">
                    <div class="card">
                        <div class="card-body">
                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <h4 class="card-title mb-0">Uso mensual {{ anio }}</h4>
                                <span v-if="loading" class="badge badge-info">Cargando...</span>
                            </div>
                            <div class="table-responsive">
                                <table class="table table-sm table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th>Mes</th>
                                            <th class="text-right">V1</th>
                                            <th class="text-right">V2</th>
                                            <th class="text-right">V3</th>
                                            <th class="text-right">Total</th>
                                            <th class="text-right">Descartadas</th>
                                            <th class="text-right">Plantilla</th>
                                            <th class="text-right">QR</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="mes in meses" :key="mes.mes">
                                            <td class="text-capitalize">{{ mes.nombre }}</td>
                                            <td class="text-right">{{ formatNumber(mes.v1) }}</td>
                                            <td class="text-right">{{ formatNumber(mes.v2) }}</td>
                                            <td class="text-right">{{ formatNumber(mes.v3) }}</td>
                                            <td class="text-right font-weight-semibold">{{ formatNumber(mes.total) }}</td>
                                            <td class="text-right">{{ formatNumber(mes.descartadas) }}</td>
                                            <td class="text-right">{{ formatNumber(mes.plantilla_asistente) }}</td>
                                            <td class="text-right">{{ formatNumber(mes.asistentes_qr) }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-lg-4">
                    <div class="card">
                        <div class="card-body">
                            <h4 class="card-title mb-3">Resumen por version</h4>
                            <div v-for="version in resumenAnual.versiones" :key="version.key" class="border-bottom pb-3 mb-3">
                                <div class="d-flex justify-content-between align-items-center">
                                    <strong>{{ version.label }}</strong>
                                    <span class="h5 mb-0">{{ formatNumber(version.total) }}</span>
                                </div>
                                <small class="text-muted d-block">{{ version.descripcion }}</small>
                                <small class="text-muted">Descartadas: {{ formatNumber(version.descartadas) }}</small>
                            </div>
                            <div class="alert alert-light mb-0">
                                V1 cuenta solo paginas frontales. V2 cuenta fichas detectadas o el placeholder del PDF. V3 cuenta una digitalizacion por pagina frontal. Las fichas procesadas y luego descartadas, vaciadas o eliminadas conservan su uso historico y no reducen estos contadores. Plantilla asistente y asistentes QR se muestran solo como referencia y no se suman al total anual.
                            </div>
                        </div>
                    </div>

                    <div class="card">
                        <div class="card-body">
                            <h4 class="card-title mb-3">Mes actual</h4>
                            <p class="mb-1">Digitalizaciones: <strong>{{ formatNumber(totalMesActual) }}</strong></p>
                            <p class="mb-1">Descartadas: <strong>{{ formatNumber(mesActual ? mesActual.descartadas : 0) }}</strong></p>
                            <p class="mb-1">Plantilla asistente: <strong>{{ formatNumber(mesActual ? mesActual.plantilla_asistente : 0) }}</strong></p>
                            <p class="mb-1">Asistentes QR: <strong>{{ formatNumber(mesActual ? mesActual.asistentes_qr : 0) }}</strong></p>
                            <p class="mb-0 text-muted">Base proyeccion: {{ formatNumber(proyeccion.meses_base) }} meses</p>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </Layout>
</template>
